import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, LOGO_LOCKUP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTestVisibility } from "@/context/TestVisibilityContext";
import { useI18n } from "@/i18n/I18nContext";
import {
  Settings as SettingsIcon, Loader2, Building2, Boxes, Eye, Languages,
  Network, Workflow, Wrench, GitBranch, Image, Activity, Layers3, Route, ShieldCheck, WandSparkles, ShieldAlert, CheckCircle2, AlertTriangle, Laptop, History, KeyRound,
} from "lucide-react";

const CAPABILITIES = [
  ["/workforce", "AI Workforce", Network],
  ["/workflows", "Workflow Engine", Workflow],
  ["/production-tools", "Production Tools", Wrench],
  ["/decisions", "Decision Log", GitBranch],
  ["/assets", "Assets", Image],
  ["/activity", "Activity / Audit Trail", Activity],
];

export default function SettingsPage() {
  const { user } = useAuth();
  const tv = useTestVisibility();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const L = (id, en) => lang === "id" ? id : en;
  const [company, setCompany] = useState(null);
  const [modules, setModules] = useState(null);
  const [playbooks, setPlaybooks] = useState(null);
  const [githubConnector, setGithubConnector] = useState(null);
  const [githubProbe, setGithubProbe] = useState(null);
  const [probingGithub, setProbingGithub] = useState(false);
  const [executionSync, setExecutionSync] = useState(null);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [productionReadiness, setProductionReadiness] = useState(null);
  const [authSessions, setAuthSessions] = useState(null);
  const [securityAudit, setSecurityAudit] = useState(null);
  const [revokingSessions, setRevokingSessions] = useState(false);

  useEffect(() => {
    api.company().then(setCompany);
    api.operatingModules("ACTIVE").then(setModules);
    api.executionPlaybooks("ACTIVE").then(setPlaybooks);
    api.githubConnectorStatus().then(setGithubConnector).catch(() => setGithubConnector({ configured: false, connector: "GITHUB_VERIFIED_TRACE_V1", mode: "READ_ONLY" }));
    api.executionSyncStatus().then(setExecutionSync).catch(() => setExecutionSync(null));
    api.automationStatus().then(setAutomationStatus).catch(() => setAutomationStatus(null));
    api.productionReadiness().then(setProductionReadiness).catch(() => setProductionReadiness(null));
    api.authSessions().then(setAuthSessions).catch(() => setAuthSessions([]));
    api.securityAudit(12).then(setSecurityAudit).catch(() => setSecurityAudit([]));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="mono-label">FOUNDER CONTROL CENTER</p>
        <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><SettingsIcon className="text-[#2563EB]" /> System</h1>
        <p className="text-sm text-[#94A3B8] mt-2">
          {L("Struktur Company OS, operating modules, execution playbooks, dan kapabilitas backend.", "Company OS structure, operating modules, execution playbooks, and backend capabilities.")}
        </p>
      </div>

      <ManualAdministration />

      <section className="m-card p-5" data-testid="system-production-readiness">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label flex items-center gap-2"><ShieldAlert size={14} className="text-[#F59E0B]" /> PRODUCTION READINESS V1</p>
            <p className="text-xs text-[#64748B] mt-1">
              {L("Gate teknis sebelum production deploy. Readiness tidak pernah menjadi izin deploy.", "Technical gate before production deployment. Readiness never authorizes deployment.")}
            </p>
          </div>
          {!productionReadiness ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
            <span className={`m-pill text-[10px] ${
              productionReadiness.technical_readiness?.status === "PASS" ? "text-[#34D399]" :
              productionReadiness.technical_readiness?.status === "BLOCKED" ? "text-[#FB7185]" :
              "text-[#FBBF24]"
            }`}>
              {productionReadiness.technical_readiness?.status || "UNKNOWN"}
            </span>
          )}
        </div>
        {productionReadiness && (
          <>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                <p className="mono-label">RUNTIME</p>
                <p className="text-white mt-2 font-semibold">{productionReadiness.runtime_environment}</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                <p className="mono-label">PASS</p>
                <p className="text-[#34D399] mt-2 text-lg font-semibold">{productionReadiness.technical_readiness?.pass_count || 0}</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                <p className="mono-label">BLOCKED</p>
                <p className="text-[#FB7185] mt-2 text-lg font-semibold">{productionReadiness.technical_readiness?.blocked_count || 0}</p>
              </div>
              <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                <p className="mono-label">FOUNDER REVIEW</p>
                <p className="text-[#FBBF24] mt-2 text-lg font-semibold">{productionReadiness.technical_readiness?.review_required_count || 0}</p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {(productionReadiness.checks || []).filter((x) => x.status !== "PASS").map((x) => (
                <div key={x.code} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 text-xs"
                  data-testid={`readiness-check-${x.code}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {x.status === "BLOCKED" ? <AlertTriangle size={13} className="text-[#FB7185]" /> : <ShieldAlert size={13} className="text-[#FBBF24]" />}
                    <span className="font-mono text-white">{x.code}</span>
                    <span className={`m-pill text-[9px] ${x.status === "BLOCKED" ? "text-[#FB7185]" : "text-[#FBBF24]"}`}>{x.status}</span>
                    <span className="text-[10px] text-[#64748B]">{x.category}</span>
                  </div>
                  <p className="text-[#94A3B8] mt-2">{x.detail}</p>
                  {x.remediation && <p className="text-[#CBD5E1] mt-1"><span className="text-[#64748B]">Next:</span> {x.remediation}</p>}
                </div>
              ))}
              {(productionReadiness.checks || []).every((x) => x.status === "PASS") && (
                <div className="flex items-center gap-2 text-xs text-[#34D399]">
                  <CheckCircle2 size={14} /> {L("Semua technical readiness check PASS.", "All technical readiness checks PASS.")}
                </div>
              )}
            </div>

            <div className="mt-4 bg-[#111827] border border-[#374151] rounded-xl p-3 text-xs">
              <p className="mono-label">DEPLOYMENT GATE</p>
              <p className="text-[#FBBF24] mt-2 font-semibold">{productionReadiness.deployment_gate?.status}</p>
              <p className="text-[#94A3B8] mt-1">{productionReadiness.deployment_gate?.message}</p>
              <p className="text-[10px] text-[#64748B] mt-2">AUTO DEPLOY · OFF · AUTO ROLLBACK · OFF</p>
            </div>
          </>
        )}
      </section>

      <section className="m-card p-5" data-testid="system-auth-security">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label flex items-center gap-2"><KeyRound size={14} className="text-[#34D399]" /> AUTH & SECURITY HARDENING V1</p>
            <p className="text-xs text-[#64748B] mt-1">
              {L("Server-side session, CSRF, Founder role guard, rate limiting, dan session revocation.", "Server-side session, CSRF, Founder role guard, rate limiting, and session revocation.")}
            </p>
          </div>
          <span className="m-pill text-[10px] text-[#34D399]">SERVER-SIDE SESSION</span>
        </div>

        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="mono-label flex items-center gap-2"><Laptop size={13} /> ACTIVE SESSIONS</p>
              <span className="text-xs font-mono text-[#94A3B8]">{authSessions ? authSessions.length : "…"}</span>
            </div>
            {!authSessions ? <Loader2 className="animate-spin text-[#2563EB] mt-3" /> : (
              <div className="space-y-2 mt-3">
                {authSessions.slice(0, 6).map((s) => (
                  <div key={s.id} className="border border-[#1E293B] rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[#E2E8F0] font-mono">{s.current ? "CURRENT SESSION" : "ACTIVE SESSION"}</span>
                      {s.current && <span className="m-pill text-[9px] text-[#34D399]">CURRENT</span>}
                    </div>
                    <p className="text-[10px] text-[#64748B] mt-1">
                      {L("Dibuat", "Created")} {s.created_at ? new Date(s.created_at).toLocaleString() : "—"} ·
                      {" "}{L("Terakhir aktif", "Last seen")} {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}
                    </p>
                  </div>
                ))}
                {authSessions.length === 0 && <p className="text-xs text-[#64748B]">{L("Tidak ada session aktif.", "No active sessions.")}</p>}
              </div>
            )}
            <button
              onClick={async () => {
                setRevokingSessions(true);
                try {
                  await api.revokeOtherSessions();
                  const rows = await api.authSessions();
                  setAuthSessions(rows);
                  setSecurityAudit(await api.securityAudit(12));
                } finally {
                  setRevokingSessions(false);
                }
              }}
              disabled={revokingSessions || !authSessions || authSessions.filter((s) => !s.current).length === 0}
              className="btn-ghost h-9 px-3 text-xs mt-3 disabled:opacity-50"
              data-testid="revoke-other-sessions">
              {revokingSessions ? L("Mencabut…", "Revoking…") : L("Cabut session lain", "Revoke other sessions")}
            </button>
          </div>

          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="mono-label flex items-center gap-2"><History size={13} /> SECURITY AUDIT</p>
              <span className="text-[10px] text-[#64748B]">SANITIZED</span>
            </div>
            {!securityAudit ? <Loader2 className="animate-spin text-[#2563EB] mt-3" /> : (
              <div className="space-y-2 mt-3">
                {securityAudit.slice(0, 8).map((e) => (
                  <div key={e.id} className="text-xs border-b border-[#1E293B] pb-2 last:border-b-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[#E2E8F0] font-mono">{e.event}</span>
                      <span className="text-[10px] text-[#64748B]">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                    </div>
                    {e.detail && <p className="text-[10px] text-[#64748B] mt-1">{typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail)}</p>}
                  </div>
                ))}
                {securityAudit.length === 0 && <p className="text-xs text-[#64748B]">{L("Belum ada security event.", "No security events yet.")}</p>}
              </div>
            )}
          </div>
        </div>
      </section>

      <FounderPasswordRotation L={L} />

      <section className="m-card p-5" data-testid="system-operating-modules">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="mono-label flex items-center gap-2"><Layers3 size={14} className="text-[#2563EB]" /> OPERATING MODULE REGISTRY</p>
            <p className="text-xs text-[#64748B] mt-1">{L("Konteks bisnis lama dipetakan ke modul terstruktur — bukan aplikasi terpisah.", "Legacy business contexts mapped into structured modules — not separate applications.")}</p>
          </div>
          <span className="text-xs font-mono text-[#94A3B8]">{modules ? modules.length : "…"}/11</span>
        </div>
        {!modules ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {modules.map((m) => (
              <div key={m.code} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4" data-testid={`operating-module-${m.code}`}>
                <div className="flex items-center justify-between gap-2"><p className="text-white text-sm font-semibold">{m.name}</p><span className="mono-label">{m.version}</span></div>
                <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed">{m.purpose}</p>
                <p className="text-[10px] text-[#64748B] mt-3 font-mono">OWNER {m.owner}</p>
                <p className="text-[10px] text-[#475569] mt-1">{(m.source_context || []).join(" · ")}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="m-card p-5" data-testid="system-execution-playbooks">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="mono-label flex items-center gap-2"><Route size={14} className="text-[#2563EB]" /> EXECUTION PLAYBOOK REGISTRY</p>
            <p className="text-xs text-[#64748B] mt-1">{L("Playbook mengarahkan cara kerja; workflow engine tetap menjadi mekanisme eksekusi.", "Playbooks guide work; the workflow engine remains the execution mechanism.")}</p>
          </div>
          <span className="text-xs font-mono text-[#94A3B8]">{playbooks ? playbooks.length : "…"}/5</span>
        </div>
        {!playbooks ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
          <div className="space-y-3">
            {playbooks.map((p) => (
              <div key={p.code} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4" data-testid={`execution-playbook-${p.code}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div><p className="text-white text-sm font-semibold">{p.name}</p><p className="mono-label mt-1">{p.domain} · v{p.version}</p></div>
                  <span className="text-[10px] text-[#64748B] font-mono">{(p.execution_tool_preference || []).join(" → ")}</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">{p.purpose}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(p.default_flow || []).map((x) => <span key={x} className="m-pill text-[10px]">{x}</span>)}
                </div>
                <p className="text-[10px] text-[#475569] mt-3">{(p.source_context || []).join(" · ")}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="m-card p-5" data-testid="system-github-connector">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label flex items-center gap-2"><ShieldCheck size={14} className="text-[#38BDF8]" /> GITHUB VERIFIED TRACE V1</p>
            <p className="text-xs text-[#64748B] mt-1">
              {githubConnector?.credential_source === "EXISTING_COOLIFY_GITHUB_APP_FILE" ? L("Terhubung lewat GitHub App Coolify yang sudah ada. Membaca repository, branch dan commit; PR/CI belum masuk izin App saat ini.", "Connected through the existing Coolify GitHub App. Repository, branch and commit reads work; PR/CI scopes are not currently granted.") : L("Connector baca GitHub dengan hak akses sesuai sumber kredensial yang aktif.", "Read-only GitHub connector, subject to its authorized credential scope.")}
            </p>
          </div>
          {!githubConnector ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
            <span className={`m-pill text-[10px] ${githubConnector.configured ? "text-[#34D399]" : "text-[#FBBF24]"}`}>
              {githubConnector.configured ? "CONFIGURED · READ ONLY" : "NOT CONFIGURED"}
            </span>
          )}
        </div>
        {githubConnector && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">CAPABILITIES</p>
              <p className="text-[#94A3B8] mt-2">{(githubConnector.capabilities || []).join(" · ") || "—"}</p>
            </div>
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">WRITE ACCESS</p>
              <p className="text-[#94A3B8] mt-2">{(githubConnector.write_capabilities || []).length ? githubConnector.write_capabilities.join(" · ") : "NONE"}</p>
              <p className="text-[10px] text-[#64748B] mt-2">
                {githubConnector.configured
                  ? L(githubConnector.credential_source === "EXISTING_COOLIFY_GITHUB_APP_FILE" ? "Akses melalui token instalasi sementara dari GitHub App Coolify. Tidak perlu token personal baru." : "Kredensial tetap di backend dan nilainya tidak ditampilkan.", githubConnector.credential_source === "EXISTING_COOLIFY_GITHUB_APP_FILE" ? "Scoped short-lived token from the existing Coolify GitHub App. No new personal token needed." : "Credential remains backend-only and is never displayed.")
                  : L("Set GITHUB_READ_TOKEN atau GITHUB_TOKEN di runtime untuk mengaktifkan verifikasi.", "Set GITHUB_READ_TOKEN or GITHUB_TOKEN at runtime to enable verification.")}
              </p>
            </div>
          </div>
        )}
        <div className="mt-4 rounded-xl border border-[#274268] bg-[#091526] p-4 text-xs" data-testid="github-read-connector-probe">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-white font-semibold">Uji koneksi repository privat</p>
              <p className="text-[#94A3B8] mt-1">sapazaku-lab/masandi-ai-labs · baca metadata dan branch saja; tidak ada perubahan GitHub.</p>
            </div>
            <button className="btn-ghost h-9 px-3 text-xs disabled:opacity-50"
              onClick={async () => {
                setProbingGithub(true);
                setGithubProbe(null);
                try { setGithubProbe(await api.githubConnectorProbe()); }
                catch { setGithubProbe({ state: "PROBE_UNAVAILABLE", verified: false }); }
                finally { setProbingGithub(false); }
              }} disabled={probingGithub} data-testid="github-probe-trigger">
              {probingGithub ? "Memeriksa…" : "Uji Koneksi"}
            </button>
          </div>
          {githubProbe && <div role="status" className="mt-3 rounded-lg border border-[#1E293B] p-3" data-testid="github-probe-result">
            <p className={githubProbe.verified ? "text-[#34D399] font-semibold" : "text-[#FBBF24] font-semibold"}>
              {githubProbe.verified ? "TERVERIFIKASI · READ ONLY" :
                githubProbe.state === "NOT_CONFIGURED" ? "BELUM TERHUBUNG — TOKEN TIDAK TERSEDIA" :
                githubProbe.state === "ACCESS_DENIED_OR_REPO_MISSING" ? "AKSES REPOSITORY BELUM DIBERIKAN" :
                "KONEKSI BELUM DAPAT DIVERIFIKASI"}
            </p>
            <p className="text-[#94A3B8] mt-1">Status: {githubProbe.state}. Status 'configured' saja tidak membuktikan izin membaca repository.</p>
          </div>}
          <div className="mt-3 space-y-2 text-[#94A3B8]">
            <p className="text-white font-medium">{githubConnector?.configured ? "Koneksi GitHub sudah tersedia" : "Periksa koneksi GitHub yang sudah ada"}</p>
            <p>MASANDI menggunakan GitHub App Coolify. Jangan membuat PAT atau akun GitHub tambahan hanya untuk mengaktifkan koneksi yang sama.</p>
            <p>Gunakan <strong>Uji Koneksi</strong> untuk memastikan repository privat bisa dibaca. Token dan private key tidak ditampilkan atau diminta dari Founder.</p>
            <p>Pemeriksaan PR dan Actions/CI membutuhkan izin tambahan jika kelak memang diperlukan. Penambahan izin tidak dilakukan otomatis.</p>
            <p>Sinkronisasi GitHub terjadwal dan eksekusi eksternal merupakan kemampuan terpisah, bukan bagian dari koneksi read-only ini.</p>
          </div>
        </div>
        {executionSync && (
          <div className="mt-3 bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 text-xs"
            data-testid="system-execution-sync">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="mono-label">EXECUTION SYNC V1</p>
                <p className="text-[#64748B] mt-1">
                  {L("On-demand selalu tersedia. Scheduled sync hanya berjalan jika diaktifkan secara eksplisit di runtime.", "On-demand sync is always available. Scheduled sync only runs when explicitly enabled at runtime.")}
                </p>
              </div>
              <span className={`m-pill text-[10px] ${executionSync.scheduled_running ? "text-[#34D399]" : "text-[#94A3B8]"}`}>
                {executionSync.scheduled_running ? "SCHEDULED RUNNING" : executionSync.scheduled_enabled ? "ENABLED · NOT RUNNING" : "SCHEDULED OFF"}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 font-mono text-[10px] text-[#94A3B8]">
              <span>INTERVAL {executionSync.interval_seconds}s</span>
              <span>MIN {executionSync.minimum_interval_seconds}s</span>
              <span>WRITE ACCESS NONE</span>
              {executionSync.last_run && <span>LAST {executionSync.last_run.status} · {executionSync.last_run.synced_count || 0} synced</span>}
            </div>
          </div>
        )}
      </section>

      <section className="m-card p-5" data-testid="system-automation-orchestration">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label flex items-center gap-2"><WandSparkles size={14} className="text-[#A78BFA]" /> AUTOMATION ORCHESTRATION V1</p>
            <p className="text-xs text-[#64748B] mt-1">
              {L("Event internal terverifikasi → suggestion atau queued tracking task. Tidak ada eksekusi eksternal otomatis.", "Verified internal events → suggestion or queued tracking task. No automatic external execution.")}
            </p>
          </div>
          {!automationStatus ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
            <span className="m-pill text-[10px] text-[#C4B5FD]">{automationStatus.mode}</span>
          )}
        </div>
        {automationStatus && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">RULES</p>
              <p className="text-white mt-2 text-lg font-semibold">{automationStatus.rules?.length || 0}</p>
            </div>
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">OPEN SUGGESTIONS</p>
              <p className="text-white mt-2 text-lg font-semibold">{automationStatus.open_suggestions || 0}</p>
            </div>
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">QUEUED AUTO TASKS</p>
              <p className="text-white mt-2 text-lg font-semibold">{automationStatus.queued_automation_tasks || 0}</p>
            </div>
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
              <p className="mono-label">AUTONOMY BOUNDARY</p>
              <p className="text-[#94A3B8] mt-2 font-mono text-[10px]">
                EXTERNAL EXEC {automationStatus.external_execution ? "ON" : "OFF"} · GITHUB WRITE {automationStatus.github_write ? "ON" : "OFF"} · AUTO MERGE {automationStatus.auto_merge ? "ON" : "OFF"} · AUTO DEPLOY {automationStatus.auto_deploy ? "ON" : "OFF"}
              </p>
            </div>
          </div>
        )}
      </section>

      <section className="m-card p-5" data-testid="system-capabilities">
        <p className="mono-label mb-3">{L("KAPABILITAS ENGINE", "ENGINE CAPABILITIES")}</p>
        <p className="text-xs text-[#64748B] mb-4">
          {L("Fitur lama tetap hidup dan diakses secara kontekstual dari Founder Control Center.", "Existing capabilities remain available and are accessed contextually from Founder Control Center.")}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {CAPABILITIES.map(([to, label, Icon]) => (
            <button key={to} onClick={() => navigate(to)} className="m-card m-card-hover p-3 text-left flex items-center gap-3">
              <Icon size={16} className="text-[#2563EB]" /><span className="text-sm text-white">{label}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="m-card p-5" data-testid="language-setting">
        <p className="mono-label mb-3 flex items-center gap-2"><Languages size={14} className="text-[#2563EB]" /> {t("settings.langTitle")}</p>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-[#64748B] max-w-xl">{t("settings.langDesc")}</p>
          <div className="flex gap-1 bg-[#0B0F19] border border-[#1E293B] rounded-full p-1 shrink-0">
            <button onClick={() => setLang("id")}
              className={`h-8 px-4 rounded-full text-xs font-semibold transition-colors ${lang === "id" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}
              data-testid="lang-toggle-id">{t("settings.langId")}</button>
            <button onClick={() => setLang("en")}
              className={`h-8 px-4 rounded-full text-xs font-semibold transition-colors ${lang === "en" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}
              data-testid="lang-toggle-en">{t("settings.langEn")}</button>
          </div>
        </div>
      </div>

      <div className="m-card p-5" data-testid="test-visibility-setting">
        <p className="mono-label mb-3 flex items-center gap-2"><Eye size={14} className="text-[#2563EB]" /> {t("settings.tvTitle")}</p>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-white">{t("settings.tvToggleLabel")}</p>
            <p className="text-xs text-[#64748B] mt-1 max-w-xl">{t("settings.tvDesc")}</p>
          </div>
          <button onClick={() => tv?.setMasterPref(!tv?.master)}
            className={`h-8 px-4 rounded-full text-xs font-semibold shrink-0 transition-colors ${tv?.master ? "bg-[#FCD34D] text-[#451A03]" : "bg-[#1E293B] text-[#94A3B8]"}`}
            data-testid="test-visibility-master-toggle">
            {tv?.master ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div className="m-card p-6">
        <img src={LOGO_LOCKUP} alt="MASANDI AI LABS" className="h-10 object-contain object-left" />
        <p className="text-xs text-[#64748B] mt-3">{t("settings.logoStatus")} <span className="text-[#FCD34D] font-mono">PROVISIONAL_APPROVED_FOR_BUILD</span> — {t("settings.logoNote")}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="m-card p-5">
          <p className="mono-label mb-3 flex items-center gap-2"><Building2 size={14} className="text-[#2563EB]" /> {t("settings.brandHierarchy")}</p>
          {!company ? <Loader2 className="animate-spin text-[#2563EB]" /> : (
            <div className="space-y-1 text-sm">
              <p className="text-[#94A3B8]">{company.parent_organization}</p>
              <p className="text-white pl-3">↳ {company.name} <span className="text-[10px] text-[#64748B] font-mono">({company.code})</span></p>
              <p className="text-white pl-6">↳ {company.primary_product}</p>
              <p className="text-white pl-9">↳ {t("settings.projectsLine")} (P001 LURI SALA, …)</p>
            </div>
          )}
        </div>
        <div className="m-card p-5">
          <p className="mono-label mb-3 flex items-center gap-2"><Boxes size={14} className="text-[#2563EB]" /> {t("settings.founderAccount")}</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">{t("settings.name")}</span><span className="text-white">{user?.name}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Email</span><span className="text-white">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">{t("settings.role")}</span><span className="text-white">{user?.role}</span></div>
          </div>
        </div>
      </div>

      <div className="m-card p-5">
        <p className="mono-label mb-3">{t("settings.invariants")}</p>
        <div className="grid sm:grid-cols-2 gap-2 text-xs font-mono text-[#94A3B8]">
          {["COMPANY ≠ PROJECT", "PROJECT ≠ AGENT", "AGENT ≠ MODEL", "MODEL ≠ TOOL", "KNOWLEDGE ≠ CHAT HISTORY", "AUTONOMY ≠ AUTHORITY", "MISSION ≠ WORKFLOW", "APPROVAL ≠ ESCALATION"].map((r) => (
            <div key={r} className="bg-[#0B0F19] border border-[#1E293B] rounded-lg px-3 py-2">{r}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ManualAdministration() {
  const [prefs, setPrefs] = useState({ dashboard_note: "", timezone: "Asia/Jakarta" });
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [roles, setRoles] = useState({});
  const [form, setForm] = useState({ name: "", email: "", role: "BUSINESS_MANAGER", business_scopes: "", function_scopes: "" });
  const [invitation, setInvitation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const refresh = async () => {
    const [p, u, a, r] = await Promise.all([api.systemPreferences(), api.systemUsers(), api.accessAssignments(), api.accessRoles()]);
    setPrefs(p); setUsers(u); setAssignments(a); setRoles(r.roles || {});
  };
  useEffect(() => { refresh().catch(() => setMessage("Pengaturan belum dapat dimuat.")); }, []);
  const run = async (action) => {
    setBusy(true); setMessage("");
    try { await action(); await refresh(); setMessage("Perubahan tersimpan."); }
    catch (error) { setMessage(error.response?.data?.detail || "Perubahan gagal disimpan."); }
    finally { setBusy(false); }
  };
  return <div className="grid xl:grid-cols-2 gap-4" data-testid="manual-administration">
    <section className="m-card p-5 space-y-4">
      <div><h2 className="font-display font-bold text-white">Pengaturan manual sistem</h2><p className="text-xs text-[#94A3B8] mt-1">Preferensi tampilan Founder tersimpan untuk semua sesi. Konfigurasi infrastruktur dan kredensial tetap dikelola di lingkungan production.</p></div>
      <label className="block text-xs text-[#94A3B8]">Catatan dashboard
        <textarea maxLength={240} value={prefs.dashboard_note || ""} onChange={e => setPrefs({ ...prefs, dashboard_note: e.target.value })} className="mt-2 w-full min-h-24 rounded-lg border border-[#29466e] bg-[#091526] text-white p-3" placeholder="Pesan internal Founder (opsional)" />
      </label>
      <label className="block text-xs text-[#94A3B8]">Zona waktu
        <select value={prefs.timezone || "Asia/Jakarta"} onChange={e => setPrefs({ ...prefs, timezone: e.target.value })} className="mt-2 w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-3"><option value="Asia/Jakarta">Asia/Jakarta (WIB)</option><option value="UTC">UTC</option></select>
      </label>
      <button disabled={busy} onClick={() => run(() => api.saveSystemPreferences(prefs))} className="rounded-lg bg-[#2563EB] px-4 py-2 text-sm text-white disabled:opacity-50">Simpan pengaturan</button>
    </section>
    <section className="m-card p-5 space-y-4">
      <div><h2 className="font-display font-bold text-white">User Management</h2><p className="text-xs text-[#94A3B8] mt-1">Undang staf dengan peran dan lingkup bisnis, lihat akun, serta cabut akses. Kode undangan berlaku 24 jam dan hanya tampil sekali.</p></div>
      <div className="space-y-2">{users.map(u => <div key={u.id} className="rounded-lg border border-[#29466e] bg-[#091526] p-3 text-sm flex items-center justify-between gap-2"><div><strong className="text-white">{u.name}</strong><span className="ml-2 text-xs text-blue-300">{u.role} · {u.status || "ACTIVE"}</span><p className="text-xs text-[#94A3B8] mt-1">{u.email}</p></div>{u.role === "STAFF" && u.status !== "DISABLED" && <button disabled={busy} onClick={() => { if (window.confirm(`Nonaktifkan akun ${u.email} dan cabut seluruh sesinya?`)) run(() => api.disableStaff(u.id)); }} className="text-xs text-rose-300">Nonaktifkan</button>}</div>)}{!users.length && <p className="text-xs text-[#94A3B8]">Belum ada akun yang dimuat.</p>}</div>
      <h3 className="text-sm font-semibold text-white">Delegasi akses</h3>
      {assignments.map(a => <div key={a.assignment_id} className="rounded-lg border border-[#29466e] bg-[#091526] p-3 text-xs flex items-start justify-between gap-3"><div><strong className="text-white">{a.user_ref}</strong><p className="text-blue-300 mt-1">{a.role} · {(a.business_scopes || []).join(", ") || "Lintas bisnis"}</p></div><button disabled={busy} onClick={() => { if (window.confirm(`Cabut delegasi ${a.user_ref}?`)) run(() => api.revokeAccessAssignment(a.assignment_id)); }} className="text-rose-300">Cabut</button></div>)}
      <form className="space-y-2" onSubmit={async e => { e.preventDefault(); setBusy(true); setMessage(""); setInvitation(""); try { const result = await api.inviteStaff({ ...form, business_scopes: form.business_scopes.split(",").map(x => x.trim()).filter(Boolean), function_scopes: form.function_scopes.split(",").map(x => x.trim()).filter(Boolean) }); setInvitation(result.invitation_token); await refresh(); setMessage("Undangan dibuat. Bagikan kode secara aman kepada staf yang dituju."); } catch (error) { setMessage(error.response?.data?.detail || "Undangan gagal dibuat."); } finally { setBusy(false); } }}>
        <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nama staf" aria-label="Nama staf" className="w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-2 text-sm" />
        <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email staf" aria-label="Email staf" className="w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-2 text-sm" />
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} aria-label="Peran delegasi" className="w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-2 text-sm">{Object.keys(roles).filter(x => x !== "FOUNDER").map(x => <option key={x}>{x}</option>)}</select>
        <input value={form.business_scopes} onChange={e => setForm({ ...form, business_scopes: e.target.value })} placeholder="Kode bisnis, pisahkan koma (mis. NGOPI_YUK)" aria-label="Lingkup bisnis" className="w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-2 text-sm" />
        <input value={form.function_scopes} onChange={e => setForm({ ...form, function_scopes: e.target.value })} placeholder="Kode fungsi (opsional)" aria-label="Lingkup fungsi" className="w-full rounded-lg border border-[#29466e] bg-[#091526] text-white p-2 text-sm" />
        <button disabled={busy || !Object.keys(roles).length} className="rounded-lg bg-[#2563EB] px-4 py-2 text-sm text-white disabled:opacity-50">Buat undangan staf</button>
      </form>
      {invitation && <div className="rounded-lg border border-amber-500/40 p-3"><p className="text-xs text-amber-200">Kode undangan sekali pakai. Staf membuka /activate dan menentukan kata sandinya sendiri.</p><code className="block break-all text-xs text-white mt-2">{invitation}</code><button onClick={() => navigator.clipboard.writeText(invitation)} className="text-xs text-blue-300 mt-2">Salin kode</button></div>}
    </section>
    {message && <p role="status" className="text-sm text-blue-200 xl:col-span-2">{message}</p>}
  </div>;
}


function FounderPasswordRotation({ L }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmation) {
      setError("Konfirmasi password baru tidak sama.");
      return;
    }
    if (newPassword.length < 16 || new TextEncoder().encode(newPassword).length > 72) {
      setError("Gunakan minimal 16 karakter dengan maksimum 72 byte UTF-8.");
      return;
    }
    setBusy(true);
    try {
      await api.changeFounderPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      window.location.replace("/login?password_updated=1");
    } catch (exception) {
      const detail = exception.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Password belum dapat diubah. Periksa sesi dan coba lagi.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="m-card p-5" data-testid="founder-password-rotation">
      <div>
        <p className="mono-label flex items-center gap-2"><KeyRound size={14} className="text-[#34D399]" /> FOUNDER PASSWORD</p>
        <p className="text-sm text-white font-semibold mt-2">{L("Ganti password secara aman", "Change password securely")}</p>
        <p className="text-xs text-[#94A3B8] mt-1">
          {L("Harus memverifikasi password saat ini. Setelah berhasil, seluruh sesi login dicabut dan Founder harus login kembali. Tidak ada password yang disimpan di browser atau ditampilkan di audit.", "Current password required. Successful rotation revokes all login sessions and requires a fresh sign-in. Passwords are never saved in browser storage or audit logs.")}
        </p>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 mt-4">
        <label className="block text-xs text-[#94A3B8]">
          {L("Password saat ini", "Current password")}
          <input type="password" required autoComplete="current-password" minLength={1}
            className="input-dark h-10 w-full px-3 mt-1" value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)} data-testid="founder-current-password" />
        </label>
        <label className="block text-xs text-[#94A3B8]">
          {L("Password baru (16+ karakter, maks. 72 byte)", "New password (16+ chars)")}
          <input type="password" required autoComplete="new-password" minLength={16} maxLength={72}
            className="input-dark h-10 w-full px-3 mt-1" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} data-testid="founder-new-password" />
        </label>
        <label className="block text-xs text-[#94A3B8]">
          {L("Konfirmasi password baru", "Confirm new password")}
          <input type="password" required autoComplete="new-password" minLength={16} maxLength={72}
            className="input-dark h-10 w-full px-3 mt-1" value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)} data-testid="founder-password-confirmation" />
        </label>
        {error && <p role="alert" className="text-[#FB7185] text-xs sm:col-span-3">{error}</p>}
        <div className="sm:col-span-3 flex justify-end">
          <button className="btn-primary h-10 px-4 text-xs disabled:opacity-50" type="submit"
            disabled={busy || !currentPassword || !newPassword || !confirmation}
            data-testid="founder-change-password-submit">
            {busy ? L("Memverifikasi…", "Verifying…") : L("Ganti Password & Cabut Semua Sesi", "Change Password & Revoke All Sessions")}
          </button>
        </div>
      </form>
    </section>
  );
}
